import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Building2, Plus, ArrowRight, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TenantSwitcher({ token, currentTenantId, onSwitch }) {
  const [tenants, setTenants] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const fetchTenants = async () => {
      const origToken = localStorage.getItem('lodgiflow_original_token');
      const tokensToTry = [...new Set([origToken, token].filter(Boolean))];

      for (const t of tokensToTry) {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/tenants`, { headers: { Authorization: `Bearer ${t}` } });
          const list = res.data.tenants || res.data || [];
          if (list.length > 1) {
            setTenants(list);
            if (origToken !== t) localStorage.setItem('lodgiflow_original_token', t);
            return; // Success
          }
        } catch {
          // Stale/expired stored token — clear it so it stops shadowing valid ones
          if (t === origToken && t !== token) localStorage.removeItem('lodgiflow_original_token');
        }
      }
    };
    fetchTenants();
  }, [token]);

  const switchTenant = async (tenantId) => {
    if (tenantId === 'all') {
      // Switch back to super admin (no tenant filter)
      const originalToken = localStorage.getItem('lodgiflow_original_token') || token;
      localStorage.setItem('lodgiflow_token', originalToken);
      localStorage.removeItem('lodgiflow_tenant_id');
      toast.success('Switched to All Tenants');
      if (onSwitch) onSwitch(originalToken, '', 'All Tenants');
      return;
    }
    try {
      // Save original token before switching
      if (!localStorage.getItem('lodgiflow_original_token')) {
        localStorage.setItem('lodgiflow_original_token', token);
      }
      const res = await axios.post(`${BACKEND_URL}/api/admin/tenants/${tenantId}/switch`, {}, { headers });
      localStorage.setItem('lodgiflow_token', res.data.token);
      localStorage.setItem('lodgiflow_tenant_id', res.data.tenant_id);
      toast.success(`Switched to ${res.data.tenant_name}`);
      if (onSwitch) onSwitch(res.data.token, res.data.tenant_id, res.data.tenant_name);
    } catch {
      toast.error('Failed to switch tenant');
    }
  };

  const createTenant = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/tenants`, { name: newName }, { headers });
      toast.success('Tenant created');
      setShowNew(false);
      setNewName('');
      const res = await axios.get(`${BACKEND_URL}/api/tenants`, { headers });
      setTenants(res.data.tenants || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="tenant-switcher">
      <div className="flex items-center gap-2 mb-2">
        <Select value={currentTenantId || 'all'} onValueChange={v => switchTenant(v)}>
          <SelectTrigger className="h-8 text-xs min-w-[140px]" data-testid="tenant-select">
            <SelectValue placeholder="All Tenants" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tenants</SelectItem>
            {tenants.map(t => (
              <SelectItem key={t.id} value={t.id}>
                <span className="flex items-center gap-2">
                  {t.name}
                  <Badge variant="secondary" className="text-[9px] font-mono">{t.property_count} props</Badge>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-8 text-xs shrink-0 hidden md:flex" onClick={() => setShowNew(true)}>
          <Plus className="h-3 w-3" />
        </Button>
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>New Tenant</DialogTitle>
            <DialogDescription>Create a new management company workspace.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs">Company Name</Label>
            <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g., Coastal Hospitality Group" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button onClick={createTenant} disabled={!newName.trim() || loading}>Create Tenant</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
